import type { FileChange, FileWrite } from '../vault/sparseWriter';
import { findDeskContainer, loadWorkspaces } from '../workspaces/loadWorkspaces';
import { refKey, withCardOpen } from '../plane/planeKey';
import type { PlaneLink, PlanePlacement, PlaneRef, StickyTint } from '../plane/planeKey';
import type { TagColorId } from '../design/tagPalette';
import { withVisibility } from '../plan/edits';
import { keeping } from '../workspaces/workspace';
import { pinAtFront, pinnedNooks, withoutPinned } from '../workspaces/pinned';
import type { Workspace } from '../workspaces/workspace';
import { deskMembers } from './desk';
import type { DeskItem } from './desk';
import type { NookPaths } from './deskContainer';
import {
  DESK_PATH,
  entryFor,
  nookPathsOf,
  readDeskContainer,
  withCardAt,
  withCardSize,
  withCardTint,
  withDeskLinks,
  withFromShelf,
  withOffDesk,
  withOnDesk,
  withShownListed,
  withShelfOrder,
  withToShelf,
  withUnlisted,
} from './deskContainer';

/** One file's part of an act: the change, made again on the file as it stands when it goes out, and
 *  what it reads again first. */
export interface DeskChange {
  path: string;
  change: FileChange;
  refresh?: FileWrite['refresh'];
  skipIfGone?: boolean;
  findMoved?: FileWrite['findMoved'];
}

type At = { x: number; y: number };

/** The nooks as the page has them when it is called. */
export type NooksNow = () => readonly Workspace[];

/**
 * The container's change, made after the cards the desk shows but no list holds are listed. What
 * the desk shows is worked out from the container and the nooks as they stand when the write goes
 * out, not when the act was made: a device may have moved a card, or pinned, filed away or nested a
 * nook, since. The nooks are read again from the vault then; until it goes, the page's text takes
 * them from [nooks]. The nooks whose own files the act changes, [own], are read as they were before
 * it, as the app reads the desk before a nook's own write. A container an older build renamed is followed to where the nooks are read
 * from, where the catalog does not know where it went; one gone by then is not made again from the
 * page's copy.
 */
function onContainer(nooks: NooksNow, change: ContainerChange, own: readonly Workspace[] = []): DeskChange {
  let fresh: readonly Workspace[] | null = null;
  return {
    path: DESK_PATH,
    skipIfGone: true,
    findMoved: findDeskContainer,
    refresh: async (vault) => {
      fresh = await loadWorkspaces(vault);
    },
    change: (raw) => {
      const before = new Map(own.map((n) => [n.path, n]));
      const now = fresh ?? nooks();
      const read = [...now.map((n) => before.get(n.path) ?? n), ...own.filter((n) => !now.some((m) => m.path === n.path))];
      const paths = nookPathsOf(read);
      const next = change(withShownListed(raw, deskMembers(readDeskContainer(raw), read).desk, paths), paths);
      return sameContainer(raw, next) ? raw : next;
    },
  };
}

/** Whether two texts of the container hold the same lists, planes and links, however they are written. */
function sameContainer(a: string, b: string): boolean {
  const lists = (raw: string) => {
    const c = readDeskContainer(raw);
    return c && JSON.stringify([c.desk.map((e) => e.text), c.shelf.map((e) => e.text), c.plane, c.links]);
  };
  return lists(a) === lists(b);
}

/** A change to the container, which reads an `id:` entry by [paths] as the nook it names. */
type ContainerChange = (raw: string, paths: NookPaths) => string;

const ownNook = (item: DeskItem): Workspace[] => (item.nook ? [item.nook] : []);

/** A nook put on the desk is pinned in its own file, at the front of the pins as they stand when the
 *  write goes out; a draft loses its mark with it. */
function pinChange(path: string, nooks: NooksNow): DeskChange {
  return { path, change: keeping((raw) => pinAtFront(raw, pinnedNooks(nooks()).map((w) => w.pinned))) };
}

/** The pin write for [path] when it names a nook that is not pinned yet. */
function pinIfNook(path: string, nooks: NooksNow): DeskChange[] {
  const nook = nooks().find((n) => n.path === path);
  return nook && nook.pinned === null ? [pinChange(path, nooks)] : [];
}

/** A card dragged on the desk: its plane line in the container. */
export function moveOnDesk(item: DeskItem, at: At, nooks: NooksNow): DeskChange[] {
  return [onContainer(nooks, (raw) => withCardAt(raw, item.ref, at))];
}

/** A note's colour chosen: its plane line in the container, placed at [spot] first where it has none. */
export function tintOnDesk(item: DeskItem, tint: StickyTint | TagColorId, spot: At, nooks: NooksNow): DeskChange[] {
  return [onContainer(nooks, (raw) => withCardTint(raw, item.ref, spot, tint))];
}

/** A sticky pulled to [size] by its corner: its plane line in the container, placed at [spot] first where it has none. */
export function sizeOnDesk(item: DeskItem, size: { w: number; h: number }, spot: At, nooks: NooksNow): DeskChange[] {
  return [onContainer(nooks, (raw) => withCardSize(raw, item.ref, spot, size))];
}

/** A connection drawn or removed: the container's `plane-links:`, one write, with the cards it joins
 *  placed where they are drawn where they had no plane line. */
export function linkOnDesk(
  before: readonly PlaneLink[],
  after: readonly PlaneLink[],
  ends: readonly { ref: PlaneRef; at: At }[],
  nooks: NooksNow,
): DeskChange[] {
  return [onContainer(nooks, (raw) => withDeskLinks(raw, before, after, ends))];
}

/** A card dragged out of the shelf panel: off `shelf:`, first in `desk:`, placed where it was let go. */
export function shelfToDesk(item: DeskItem, at: At, nooks: NooksNow): DeskChange[] {
  return [
    onContainer(nooks, (raw, paths) => withCardAt(withFromShelf(raw, item.entry, paths), item.ref, at)),
    ...pinIfNook(item.ref.ref, nooks),
  ];
}

/** A card dragged onto the shelf panel: off `desk:`, first in `shelf:`, its plane line kept; a pin is dropped. */
/** A card from the vault put on the desk at [at]: listed at the front of `desk:` and placed; a nook is pinned too. */
export function placeOnDesk(ref: PlaneRef, at: At, nooks: NooksNow): DeskChange[] {
  return [
    onContainer(nooks, (raw, paths) => withCardAt(withOnDesk(raw, entryFor(ref), paths), ref, at)),
    ...pinIfNook(ref.ref, nooks),
  ];
}

export function deskToShelf(item: DeskItem, nooks: NooksNow): DeskChange[] {
  const acts: DeskChange[] = [onContainer(nooks, (raw, paths) => withToShelf(raw, item.entry, paths), ownNook(item))];
  if (item.nook && item.pinned) acts.push({ path: item.nook.path, change: withoutPinned });
  return acts;
}

/** A card taken off the table: off `desk:`, its plane line kept for when it is put back. The thing
 *  itself is untouched, wherever else it is. */
export function offDesk(item: DeskItem, nooks: NooksNow): DeskChange[] {
  return [onContainer(nooks, (raw, paths) => withOffDesk(raw, item.key, paths))];
}

/** A recording or a note filed away from the tray: its own `visibility:` line, and off the desk and
 *  the shelf where either lists it. */
export function archiveFromDesk(ref: PlaneRef, listed: boolean, nooks: NooksNow): DeskChange[] {
  const acts: DeskChange[] = [{ path: ref.ref, change: (raw) => withVisibility(raw, 'archived') }];
  if (listed) acts.push(onContainer(nooks, (raw, paths) => withUnlisted(raw, refKey(ref), paths)));
  return acts;
}

/** A draft dragged out of the tray is kept: pinned, which takes its `draft:` line, listed if it was not, and placed. */
export function keepDraft(item: DeskItem, at: At, nooks: NooksNow): DeskChange[] {
  const acts: DeskChange[] = [
    onContainer(nooks, (raw, paths) => withCardAt(withOnDesk(raw, item.entry, paths), item.ref, at), ownNook(item)),
  ];
  if (item.nook) acts.push(pinChange(item.nook.path, nooks));
  return acts;
}

/** The shelf panel's order, as the keys of its cards. */
/** A draft let go: its own file is filed away, which takes it off the desk. Nothing closes up behind
 *  it, since no other card is moved. */
export function letGoDraft(item: DeskItem): DeskChange[] {
  return item.nook ? [{ path: item.nook.path, change: (raw) => withVisibility(raw, 'archived') }] : [];
}

export function reorderShelf(order: readonly string[], nooks: NooksNow): DeskChange[] {
  return [onContainer(nooks, (raw, paths) => withShelfOrder(raw, order, paths))];
}

/** A pin taken off a nook on the desk: it leaves the desk, back to the shelf if that lists it, else to the list. */
export function unpinOnDesk(item: DeskItem, nooks: NooksNow): DeskChange[] {
  return item.nook ? unpinNook(item.nook, nooks) : [onContainer(nooks, (raw) => raw)];
}

/** A pin taken off a nook while the desk has no container: its own file alone. With no list to keep
 *  it on, it leaves the desk. */
export function unpinWithoutContainer(item: DeskItem): DeskChange[] {
  return item.nook ? [{ path: item.nook.path, change: withoutPinned }] : [];
}

/**
 * A pin taken off [nook], from any page: its own file loses `pinned:` and the container's `desk:`
 * loses its entry, so it is no longer on the table and never a draft again.
 */
export function unpinNook(nook: Workspace, nooks: NooksNow): DeskChange[] {
  return [{ path: nook.path, change: withoutPinned }, unlistedOnUnpin(nook, nooks)];
}

/** [unpinNook]'s change to the container alone, for a page that writes the nook's own file itself. */
export function unlistedOnUnpin(nook: Workspace, nooks: NooksNow): DeskChange {
  const key = refKey({ ref: nook.path, anchors: [] });
  return onContainer(nooks, (raw, paths) => withOffDesk(raw, key, paths), [nook]);
}

/**
 * A card opened (drawn open, [lines] tall as measured) or closed ([lines] tall as its collapsed
 * estimate). A card with no plane line yet is placed at [spot] first, since an open card is the
 * person's to place.
 */
export function openOnDesk(item: DeskItem, spot: At, open: boolean, nooks: NooksNow, lines?: number): DeskChange[] {
  return [
    onContainer(nooks, (raw) => {
      const placed = readDeskContainer(raw)?.plane.some((p: PlanePlacement) => refKey(p) === item.key);
      const base = placed ? raw : withCardAt(raw, item.ref, spot);
      return withCardOpen(base, item.key, open, lines);
    }),
  ];
}

let made = 0;

/**
 * [acts] as the writes the queue takes: each file's text as [current] holds it now, then changed.
 * They share a key no other act has, so a file of theirs a newer app wrote lets go of the others and
 * of nothing else.
 */
export function asWrites(acts: readonly DeskChange[], current: (path: string) => string | null) {
  const key = `desk ${++made}`;
  return acts.flatMap((act) => {
    const raw = current(act.path);
    if (raw === null) return [];
    const text = act.change(raw);
    if (text === raw) return [];
    return [
      {
        path: act.path,
        text,
        change: act.change,
        ...(act.refresh ? { refresh: act.refresh } : {}),
        ...(act.skipIfGone ? { skipIfGone: true } : {}),
        ...(act.findMoved ? { findMoved: act.findMoved } : {}),
        act: key,
      },
    ];
  });
}
